import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Snackbar } from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { getFailedTransactions } from "../../api/digitalPayAPI";
import { useSnackbar } from "../../hooks/useSnackbar";
import { formatEpoch } from "../../utils/dateUtils";
import {
  balanceTypeLabel,
  formatCardNo,
  formatKurus,
  transactionTypeLabel,
} from "./constants";
import {
  createAndDownloadExcel,
  generateExcelFilename,
} from "../../utils/excelExportUtils";
import { GhostButton, ListGrid, Panel } from "./ui";

const HEADER_NAMES = {
  idfailedTransaction: "Hata ID",
  epoch: "Tarih",
  transactionId: "İşlem No",
  error: "Hata",
  errorMessage: "Hata Mesajı",
  message: "Mesaj",
  description: "Açıklama",
  personalId: "Personel ID",
  nameSurname: "Personel",
  cardNo: "Kart No",
  departmentName: "Departman",
  firmName: "Firma",
  firmId: "Firma ID",
  nid: "Otomat No",
  nodeName: "Otomat Adı",
  productNo: "Slot No",
  productName: "Ürün",
  amountType: "Bakiye Tipi",
  transactionTypeId: "İşlem Tipi",
  previousBalance: "Önceki Bakiye",
  amount: "Tutar",
  postBalance: "Kalan Bakiye",
  updateTime: "Güncellenme Zamanı",
  craeteTime: "Oluşturulma Zamanı",
};

const humanizeField = (field) =>
  String(field)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/^./, (letter) => letter.toLocaleUpperCase("tr-TR"));

const formatValue = (value, field, cardNoHex) => {
  if (value === null || value === undefined) return "";
  const normalizedField = field.toLocaleLowerCase("en-US");
  if (normalizedField === "cardno") return formatCardNo(value, cardNoHex);
  if (normalizedField.includes("epoch")) return formatEpoch(value);
  if (normalizedField === "updatetime" || normalizedField === "craetetime") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString("tr-TR");
  }
  if (normalizedField === "amounttype") return balanceTypeLabel(value);
  if (normalizedField === "transactiontypeid") return transactionTypeLabel(value);
  if (
    normalizedField.includes("amount") ||
    normalizedField.includes("balance") ||
    normalizedField.includes("price")
  ) {
    return formatKurus(value);
  }
  if (typeof value === "object") return JSON.stringify(value);
  return value;
};

export default function ErrorsPanel({ lookups }) {
  const { cardNoHex = false } = lookups || {};
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getFailedTransactions();
      setRows(
        result
          .map((row, index) => ({
            ...(row && typeof row === "object" ? row : { value: row }),
            __errorRowId:
              row?.idfailedTransaction ??
              row?.idtransaction ??
              row?.idTransaction ??
              row?.id ??
              index,
          }))
          .sort((a, b) => (Number(b.epoch) || 0) - (Number(a.epoch) || 0))
      );
    } catch (error) {
      setRows([]);
      showSnackbar(error.message || "Hatalı işlemler alınamadı", "error");
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(() => {
    const keys = new Set();
    rows.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (key !== "__errorRowId") keys.add(key);
      });
    });

    return Array.from(keys)
      .sort((a, b) => {
        if (a === "transactionId") return 1;
        if (b === "transactionId") return -1;
        if (a === "epoch") return -1;
        if (b === "epoch") return 1;
        return a.localeCompare(b, "tr");
      })
      .map((field) => ({
        field,
        headerName: HEADER_NAMES[field] || humanizeField(field),
        flex: 1,
        minWidth: field.toLocaleLowerCase("en-US").includes("message") ? 240 : 125,
        valueFormatter: (value) => formatValue(value, field, cardNoHex),
      }));
  }, [rows, cardNoHex]);

  const handleExport = () => {
    const result = createAndDownloadExcel(
      rows,
      columns,
      "Hatalı İşlemler",
      generateExcelFilename("Vendai_V-Pay_Hatali_Islemler"),
      {
        valueTransformer: (value, column) =>
          formatValue(value, column.field, cardNoHex),
      }
    );
    if (result.success) showSnackbar(`İndirildi: ${result.fileName}`, "success");
    else showSnackbar(result.error || "Dışa aktarılamadı", "error");
  };

  return (
    <>
      <Panel
        title="Hatalı İşlemler"
        sub={`${rows.length} hata kaydı`}
        action={
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <GhostButton
              startIcon={<RefreshRoundedIcon />}
              onClick={load}
              disabled={loading}
            >
              Yenile
            </GhostButton>
            <GhostButton
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              disabled={!rows.length}
            >
              Dışa Aktar
            </GhostButton>
          </Box>
        }
      >
        <Box sx={{ mt: 0.5 }}>
          <ListGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.__errorRowId}
            loading={loading}
            height={560}
          />
        </Box>
      </Panel>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={hideSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={hideSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
